import * as Sentry from "@sentry/react";
import React from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Spacer from "../../../components/Spacer";
import CreateEditRole from "./CreateEditRole";
import RoleDetail from "./RoleDetail";
import RolesTab from "./RolesTab";

import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { rbacPermissionsQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

import AddEmployeesPage from "./RoleDetail/EmployeesTab/AddEmployeesPage";

interface RolesAdminProps {
	tab: "roles" | "create" | "edit" | "detail" | "add-employees";
}

export const rolesLoader = () => {
	const queries: Array<() => SimplifiedQuery> = [() => rbacPermissionsQuery("roles")];

	return dataLoader([queries]);
};

const RolesAdmin = (props: RolesAdminProps) => {
	const { tab } = props;
	const {
		value: ffCompetenciesEnabled,
		query: { isLoading: isLoadingCompetenciesFF },
	} = useFeatureFlagQuery("ff-is-competency-management-enabled", false);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("roles");

	const isLoadingAny = isLoadingCompetenciesFF || isRbacLoading;

	const hasViewPermission = rbacPermissions?.includes("roles:viewAdmin");

	const renderInfoBox = (heading: string, message: string) => (
		<div>
			<AdminTitle includeSpacer={false}>{heading}</AdminTitle>
			<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
				<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
				<div className="font-semibold">{message}</div>
			</div>
		</div>
	);

	const renderGatedContent = (): React.ReactNode => {
		if (isLoadingAny) return null;
		if (!ffCompetenciesEnabled) {
			return renderInfoBox("Roles", "This feature is not enabled for your organization.");
		}
		if (!hasViewPermission) {
			return renderInfoBox("Roles", "Missing required permissions.");
		}
		switch (tab) {
			case "roles":
				return (
					<>
						<Spacer height={4} />
						<RolesTab />
					</>
				);
			case "create":
				return <CreateEditRole mode="create" />;
			case "edit":
				return <CreateEditRole mode="edit" />;
			case "detail":
				return <RoleDetail />;
			case "add-employees":
				return <AddEmployeesPage />;
		}
	};

	const isFormPage = tab === "create" || tab === "edit";
	const useFullBleed = isFormPage && ffCompetenciesEnabled && !isLoadingAny && hasViewPermission;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			{useFullBleed ? (
				<>
					<div className="fixed inset-0 -z-10 bg-neutral-1" aria-hidden="true" />
					{renderGatedContent()}
				</>
			) : (
				<AdminContainer>{renderGatedContent()}</AdminContainer>
			)}
		</Sentry.ErrorBoundary>
	);
};

export default RolesAdmin;
